import useRouter from "@stadium/hooks/lib/use-custom-route";
import { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { useSelector } from "react-redux";

import { useAuth0V2 } from "@stadium/services/auth0";
import AuthLogin from "@components/auth/buttons/login";

import { InternalButton } from "@sharedComponents/buttons";
import { EventLocationProvider } from "@client/wrappers";

import Multimedia from "@sharedComponents/multimedia";

import useStoreCurrencyConverter from "@hooks/currency/use-store-currency-converter";

import { usePreviewModeContext } from "@contexts/preview-mode-context";
import { useStoreContext } from "@components/store/provider";
import { useSendItemsContext } from "@contexts/send-items-context";

import { getCartItemsCount, getCartOrderNumber } from "@selectors/cart";
import { getIsLockerShop, getIsPricelessShop } from "@selectors/store";

import { getProductAllVariantImages } from "@helpers/product-details";
import { getLineItemImage } from "@helpers/orders/cart";
import { isSwagProduct } from "@stadium/helpers/src/product/utils";

import { CHECKOUT_TARGET } from "@constants/analytics/cart";
import { getCartRoute, getCheckoutRoute } from "@constants/routes";
import { ADDED_TO_BAG_EVENT_LOCATION, MODAL_VIEW_TARGET } from "./constant";

import NoImage from "@assets/images/no-image-portrait.svg";
import modal from "./main.module.scss";

const delay = 3000;

const useAddedToBagModal = () => {
  const router = useRouter();
  const { formatPriceInDisplayStoreCurrency } = useStoreCurrencyConverter();
  const { previewMode } = usePreviewModeContext();
  const { isSendToOthers } = useSendItemsContext();

  const { storeBasePath, storeCurrencyCode, storeNumber } = useStoreContext();
  const orderNumber = useSelector(getCartOrderNumber);
  const cartItemsCount = useSelector(getCartItemsCount);
  const isLockerShop = useSelector(getIsLockerShop);
  const { isAuthenticated } = useAuth0V2();
  const isPricelessShop = useSelector(getIsPricelessShop);

  const [isOpen, setIsOpen] = useState(false);
  const [modalData, setModalData] = useState({});

  const closeModal = () => {
    setIsOpen(false);
    setTimeout(() => setModalData({}), 500);
  };

  const toggleAddedToBagModal = ({
    product = {},
    quantity = 1,
    variant = {},
    options = {},
    totalPrice,
    isCheckoutBtnRequired = true,
  } = {}) => {
    if (isOpen) {
      closeModal();
      return;
    }
    const priceIn = variant.priceIn || product.priceIn;
    const { displayName: vendor } = product.brand || {};

    //In the case of the Send to Others flow, show the available product variant image.
    const variantImage =
      isSendToOthers && isSwagProduct(product.intent)
        ? getProductAllVariantImages(product.variantsIncludingMaster)?.[0]
        : null;

    const image = variantImage ?? getLineItemImage(variant, product);
    const name = product.name;

    setModalData({
      priceIn,
      vendor,
      showVendor: !!vendor,
      image,
      name,
      quantity,
      options,
      totalPrice,
      isCheckoutBtnRequired,
    });
    setIsOpen(!isOpen);
  };

  //auto close modal after set delay
  useEffect(() => {
    let timeout;
    if (isOpen) timeout = setTimeout(() => setIsOpen(true), delay);
    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [isOpen, setIsOpen]);

  const redirectToCheckout = () => router.push(getCheckoutRoute({ storeBasePath, orderNumber }));

  const redirectToCart = () => router.push(getCartRoute({ storeBasePath }));

  const checkoutEventProps = {
    track: true,
    target: CHECKOUT_TARGET,
    eventProperties: { shop_number: storeNumber },
  };

  const renderViewBagBtn = ({ className = modal.viewBagBtn } = {}) => (
    <InternalButton
      className={className}
      onClick={redirectToCart}
      styleType="primary-outline"
      eventLocationAnalytics={{
        track: true,
        target: MODAL_VIEW_TARGET,
        eventProperties: { shop_number: storeNumber },
      }}
    >
      {`View Bag (${cartItemsCount})`}
    </InternalButton>
  );

  const renderCheckoutBtn = () =>
    isAuthenticated || previewMode ? (
      <InternalButton
        onClick={redirectToCheckout}
        className={modal.checkoutBtn}
        eventLocationAnalytics={checkoutEventProps}
      >
        Checkout
      </InternalButton>
    ) : (
      <AuthLogin returnTo={getCartRoute({ storeBasePath })}>
        <InternalButton className={modal.checkoutBtn} eventLocationAnalytics={checkoutEventProps}>
          Checkout
        </InternalButton>
      </AuthLogin>
    );

  const renderModalCTAs = ({ isCheckoutBtnRequired = true } = {}) => (
    <>
      <div className={modal.colModal}>
        {isLockerShop ? renderViewBagBtn({ className: modal.checkoutBtn }) : renderViewBagBtn()}
      </div>
      {isCheckoutBtnRequired && !isLockerShop && (
        <div className={modal.colModal}>{renderCheckoutBtn()}</div>
      )}
    </>
  );

  const renderAddedToBagModal = () => {
    const {
      priceIn,
      vendor,
      showVendor,
      image,
      name,
      quantity,
      options = {},
      totalPrice,
      isCheckoutBtnRequired = true,
    } = modalData;

    const { renderCustomCTAs, customVendorName, hidePrice } = options;

    return (
      <EventLocationProvider location={ADDED_TO_BAG_EVENT_LOCATION}>
        <Modal
          data-acsb-overlay="popup"
          show={isOpen}
          animation={true}
          className={modal.addedToBagModal}
          onHide={closeModal}
        >
          <Modal.Body className={modal.modalBody}>
            <article className={modal.addedToBag}>
              <div className={modal.articleRow}>
                <div className={modal.articleCol}>
                  <div className={modal.articleFlex}>
                    <i className={modal.iconCheckCircle} />
                    <span className={modal.addBagText}>Added to bag</span>
                  </div>
                </div>
                <div className={modal.colAuto}>
                  <a className={modal.iconClose} onClick={closeModal} />
                </div>
              </div>

              <div className={modal.modalRow4}>
                <div className={modal.colAuto}>
                  <Multimedia
                    type="image"
                    src={image || NoImage}
                    alt="Not Found"
                    height={80}
                    width={80}
                    className={`${modal.bagImage} ${
                      image ? modal.imageAvailable : modal.imageNotAvailable
                    }`}
                  />
                </div>
                <article className={modal.colModal}>
                  <div className={modal.prMd3}>
                    {customVendorName ? (
                      <p className={modal.vendorText}>{customVendorName}</p>
                    ) : (
                      showVendor && <p className={modal.vendorText}>{vendor}</p>
                    )}
                    <p className={modal.vendorName}>{name}</p>
                    <div className={modal.qty}>QTY: {quantity}</div>
                  </div>
                </article>
                {!hidePrice && !isPricelessShop && (
                  <div className={modal.colCurrency}>
                    <span>
                      {formatPriceInDisplayStoreCurrency(
                        totalPrice || priceIn * quantity,
                        storeCurrencyCode,
                      )}
                    </span>
                  </div>
                )}
              </div>

              <div className={modal.modalRow8}>
                {typeof renderCustomCTAs == "function" ? (
                  <div className={modal.colModalHalf}>{renderCustomCTAs()}</div>
                ) : (
                  renderModalCTAs({
                    isCheckoutBtnRequired: !isPricelessShop && isCheckoutBtnRequired,
                  })
                )}
              </div>
            </article>
          </Modal.Body>
        </Modal>
      </EventLocationProvider>
    );
  };

  return { toggleAddedToBagModal, renderAddedToBagModal };
};

export default useAddedToBagModal;
